import { useMemo } from 'react'

export function useSafeDate() {
  // Create consistent date formatting functions
  const formatTime = useMemo(() => {
    return (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        // Use UTC to ensure consistency between server and client
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          timeZone: 'UTC'
        })
      } catch (error) {
        return 'Invalid time'
      }
    }
  }, [])

  const formatDate = useMemo(() => {
    return (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        // Use a consistent date format that doesn't depend on client timezone
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC'
        })
      } catch (error) {
        return 'Invalid date'
      }
    }
  }, [])

  const formatRelativeTime = useMemo(() => {
    return (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        const now = new Date()
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

        if (diffInHours < 1) {
          return 'Just now'
        } else if (diffInHours < 24) {
          return `${Math.floor(diffInHours)}h ago`
        } else if (diffInHours < 48) {
          return 'Yesterday'
        } else {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
        }
      } catch (error) {
        return 'Invalid date'
      }
    }
  }, [])

  const formatDateWithToday = useMemo(() => {
    return (timestamp: string) => {
      try {
        const date = new Date(timestamp)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        // Use date strings for comparison to avoid timezone issues
        const dateStr = date.toDateString()
        const todayStr = today.toDateString()
        const yesterdayStr = yesterday.toDateString()

        if (dateStr === todayStr) {
          return 'Today'
        } else if (dateStr === yesterdayStr) {
          return 'Yesterday'
        } else {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
        }
      } catch (error) {
        return 'Invalid date'
      }
    }
  }, [])

  return {
    formatTime,
    formatDate,
    formatRelativeTime,
    formatDateWithToday
  }
}
